import { Fragment } from 'react';

import { Icon, Text, cn } from '@eluve/components';
import type { PlanPhaseSpec } from '@eluve/plan-shapes';

import { ActivityList } from './ActivityList';
import { MarkdownProse } from './MarkdownProse';
import { scaleAccent } from './accents';
import { formatCadence, formatDuration } from './format';
import { Eyebrow, IconTile, MonoChip, Panel, PanelInset } from './panel';

// PhaseRibbon — the phased shape's render primitive: a horizontal stepper of
// the phase spine plus a detail card per phase, colored along the accent
// progression scale (warm start → cool end-state). Template skeleton only (no
// patient data); every layer beyond the spine is optional and omitted cleanly.
export function PhaseRibbon({ phases }: { phases: PlanPhaseSpec[] }) {
  return (
    <div className="space-y-5">
      {/* The ribbon: ordered Panels with a top accent edge. */}
      <ol className="flex flex-wrap items-stretch gap-y-2">
        {phases.map((phase, i) => {
          const accent = scaleAccent(i, phases.length);
          return (
            <Fragment key={i}>
              {i > 0 && (
                <li
                  aria-hidden
                  className="flex items-center px-0.5 text-muted-foreground/40"
                >
                  <Icon name="ChevronRight" size="auto" className="size-4" />
                </li>
              )}
              <li className="min-w-36 flex-1">
                <Panel
                  className={cn(
                    'flex h-full flex-col gap-0.5 px-3.5 py-2.5',
                    accent.edgeT,
                  )}
                >
                  <Eyebrow className={accent.text}>Phase {i + 1}</Eyebrow>
                  <Text inline>{phase.title}</Text>
                  {phase.durationDays !== undefined && (
                    <Text
                      inline
                      size="xs"
                      color="secondary"
                      className="font-mono"
                    >
                      ~{formatDuration(phase.durationDays)}
                    </Text>
                  )}
                </Panel>
              </li>
            </Fragment>
          );
        })}
      </ol>

      {/* Per-phase detail: left accent edge, numbered tile, tinted chips. */}
      <ol className="space-y-3">
        {phases.map((phase, i) => {
          const accent = scaleAccent(i, phases.length);
          return (
            <li key={i}>
              <Panel
                className={cn(
                  'flex items-start gap-3 px-4 py-3.5',
                  accent.edgeL,
                )}
              >
                <IconTile className={accent.tile}>
                  <Text inline weight="semibold" color="inherit">
                    {i + 1}
                  </Text>
                </IconTile>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Text inline>{phase.title}</Text>
                    {phase.durationDays !== undefined && (
                      <MonoChip className={accent.tile}>
                        ~{formatDuration(phase.durationDays)}
                      </MonoChip>
                    )}
                    {phase.cadence && (
                      <MonoChip className={accent.tile}>
                        <Icon name="CalendarClock" size="auto" />
                        {formatCadence(phase.cadence)}
                      </MonoChip>
                    )}
                  </div>
                  {phase.description && (
                    <MarkdownProse
                      content={phase.description}
                      className="mt-1 max-w-2xl text-sm text-muted-foreground"
                    />
                  )}
                  {phase.activities && phase.activities.length > 0 && (
                    <ActivityList
                      activities={phase.activities}
                      accent={accent}
                      className="mt-3 max-w-2xl"
                    />
                  )}
                  {phase.exitCriteria && (
                    // Tinted accent callout so the exit gate reads as phase
                    // structure, not as a continuation of the description prose.
                    <PanelInset
                      className={cn(
                        'mt-3 max-w-2xl px-3.5 py-2.5',
                        accent.tile,
                      )}
                    >
                      <div className="flex items-center gap-1.5">
                        <Icon
                          name="Flag"
                          size="auto"
                          className="size-3.5 shrink-0"
                        />
                        <Eyebrow className={accent.text}>Exit when</Eyebrow>
                      </div>
                      <MarkdownProse
                        content={phase.exitCriteria}
                        className="mt-1 text-sm text-foreground"
                      />
                    </PanelInset>
                  )}
                </div>
              </Panel>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
